import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";
import SeoHead from "@/components/SeoHead";

const DataDisclaimerPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-amber-50" data-testid="data-disclaimer-page">
      <SeoHead
        title="Data Disclaimer"
        description="Data Disclaimer for PublicBeachAccess.com. Beach information including addresses, amenities, and ratings is sourced from public data and may not reflect current conditions. Lifeguard presence is not guaranteed. Always verify details before visiting."
        path="/data-disclaimer"
      />
      {/* Header */}
      <div className="bg-[#0EA5E9] text-white py-12">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/">
            <Button variant="ghost" className="text-white hover:bg-white/20 mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
          <h1 className="text-3xl sm:text-4xl font-extrabold font-['Outfit']">
            Data Disclaimer
          </h1>
          <p className="text-white/80 mt-2">Effective Date: April 1, 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 sm:p-12">
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 mb-8">
              Welcome to PublicBeachAccess.com ("Website"), operated by Digital Properties, LLC ("we," "our," or "us"). This Data Disclaimer outlines important information regarding the accuracy, completeness, and use of the information provided on this Website.
            </p>

            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mt-8 mb-4">1. General Information Only</h2>
            <p className="text-gray-600 mb-6">
              All information provided on this Website is for general informational and convenience purposes only. While we strive to keep information accurate and up to date, Digital Properties, LLC makes no guarantees, representations, or warranties of any kind—express or implied—about the completeness, accuracy, reliability, suitability, or availability of any information contained on the Website.
            </p>

            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mt-8 mb-4">2. No Guarantee of Accuracy</h2>
            <p className="text-gray-600 mb-4">
              Information regarding beach access points, including but not limited to:
            </p>
            <ul className="list-disc list-inside text-gray-600 mb-4 space-y-1">
              <li>Locations</li>
              <li>Parking availability (free or paid)</li>
              <li>Amenities (bathrooms, showers, accessibility features)</li>
              <li>Hours of operation</li>
              <li>Conditions or restrictions</li>
            </ul>
            <p className="text-gray-600 mb-6">
              may change at any time without notice. We do not guarantee that any listed location is open, accessible, safe, or available at the time of your visit.
            </p>

            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mt-8 mb-4">3. Third-Party and Public Data Sources</h2>
            <p className="text-gray-600 mb-4">
              The information displayed on this Website may be sourced from:
            </p>
            <ul className="list-disc list-inside text-gray-600 mb-4 space-y-1">
              <li>Publicly available government datasets</li>
              <li>Geographic information systems (GIS)</li>
              <li>Open-source platforms</li>
              <li>Third-party providers</li>
            </ul>
            <p className="text-gray-600 mb-6">
              We do not control or verify all such data and are not responsible for inaccuracies, errors, or omissions originating from these sources.
            </p>

            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mt-8 mb-4">4. Use at Your Own Risk</h2>
            <p className="text-gray-600 mb-4">
              Your use of any information from this Website is strictly at your own risk. You are solely responsible for verifying all information prior to relying on it, including confirming:
            </p>
            <ul className="list-disc list-inside text-gray-600 mb-4 space-y-1">
              <li>Access availability</li>
              <li>Local regulations and restrictions</li>
              <li>Safety conditions</li>
            </ul>
            <p className="text-gray-600 mb-6">
              Digital Properties, LLC shall not be liable for any loss, injury, inconvenience, or damages resulting from the use of this Website or reliance on its content.
            </p>

            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mt-8 mb-4">5. No Professional Advice</h2>
            <p className="text-gray-600 mb-6">
              The information provided on this Website does not constitute legal, safety, travel, or professional advice of any kind. Users should consult appropriate local authorities or professionals where necessary.
            </p>

            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mt-8 mb-4">6. External Links</h2>
            <p className="text-gray-600 mb-6">
              This Website may contain links to third-party websites for additional information or convenience. We do not control, endorse, or assume responsibility for the content, accuracy, or practices of any third-party sites.
            </p>

            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mt-8 mb-4">7. No Warranties</h2>
            <p className="text-gray-600 mb-4">
              All content on this Website is provided "as is" and "as available," without warranties of any kind, either express or implied, including but not limited to:
            </p>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-1">
              <li>Implied warranties of merchantability</li>
              <li>Fitness for a particular purpose</li>
              <li>Non-infringement</li>
            </ul>

            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mt-8 mb-4">8. Limitation of Liability</h2>
            <p className="text-gray-600 mb-4">
              To the fullest extent permitted by law, Digital Properties, LLC shall not be liable for any direct, indirect, incidental, consequential, or special damages arising out of or in connection with:
            </p>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-1">
              <li>Use of the Website</li>
              <li>Reliance on any information provided</li>
              <li>Inability to access or use the Website</li>
            </ul>

            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mt-8 mb-4">9. Updates to This Disclaimer</h2>
            <p className="text-gray-600 mb-6">
              We reserve the right to modify or update this Data Disclaimer at any time without prior notice. Changes will be effective immediately upon posting to this page.
            </p>

            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mt-8 mb-4">10. Contact</h2>
            <p className="text-gray-600 mb-6">
              If you have any questions regarding this Data Disclaimer, please contact us at{" "}
              <a href="mailto:hello.digitalproperties@gmail.com" className="text-[#0EA5E9] hover:underline">hello.digitalproperties@gmail.com</a>{" "}
              or through our <Link to="/contact" className="text-[#0EA5E9] hover:underline">Contact Page</Link>.
            </p>

            <div className="bg-[#FEF3C7] rounded-xl p-6 mt-8">
              <p className="text-gray-700 font-medium mb-2">
                By using PublicBeachAccess.com, you acknowledge and agree to this Data Disclaimer.
              </p>
              <p className="text-gray-600 text-sm italic">
                Information is deemed reliable but not guaranteed. Lifeguard presence is not guaranteed and may vary. Please verify all information before your visit.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default DataDisclaimerPage;
